import request from '@utils/axios-utils';
import { useQuery, useMutation, useQueryClient, UseQueryOptions } from 'react-query';
import { ApiObject, ApiList, CommonHash } from '@types';
import Notification from '@components/notification/notification';
import { useAuth } from '@providers/auth';

export function useCompanies() {
  const { session } = useAuth();
  return useQuery<ApiList<CommonHash>>(
    ['companies'],
    async () => {
      const response = await request({
        authorizationToken: session?.token,
        url: `/companies`,
        method: 'GET',
      });
      return response.data;
    },
    {
      enabled: session.status != 'loading',
    },
  );
}

export function useCompaniesPaginated({
  page,
  taxId,
  legalName,
  view,
}: {
  page: number;
  taxId: string;
  legalName: string;
  view?: string;
}) {
  const { session } = useAuth();
  return useQuery<ApiList<CommonHash>>(
    ['companies', 'paginated', page, taxId, legalName, view],
    async () => {
      const response = await request({
        authorizationToken: session?.token,
        url: `/companies`,
        method: 'GET',
        params: {
          page,
          ...(taxId ? { tax_id: taxId } : {}),
          ...(legalName ? { legal_name: legalName } : {}),
          ...(view ? { view } : {}),
        },
      });
      return response.data;
    },
    {
      enabled: session.status != 'loading',
      keepPreviousData: true,
    },
  );
}

export function useCompaniesBySearch(legalName: string, options?: { enabled?: boolean }) {
  const { session } = useAuth();
  const { enabled = true } = options || {};
  return useQuery<ApiList<CommonHash>>(
    ['companies', 'search', legalName],
    async () => {
      const response = await request({
        authorizationToken: session?.token,
        url: `/companies`,
        method: 'GET',
        params: legalName ? { legal_name: legalName } : {},
      });
      return response.data;
    },
    {
      enabled: session.status != 'loading' && enabled,
      keepPreviousData: true,
    },
  );
}

export function useCompaniesByTaxId(taxId: string, options?: { enabled?: boolean }) {
  const { session } = useAuth();
  const { enabled = true } = options || {};
  return useQuery<ApiList<CommonHash>>(
    ['companies', 'search', 'tax_id', taxId],
    async () => {
      const response = await request({
        authorizationToken: session?.token,
        url: `/companies`,
        method: 'GET',
        params: taxId ? { tax_id: taxId } : {},
      });
      return response.data;
    },
    {
      enabled: session.status != 'loading' && enabled,
      keepPreviousData: true,
    },
  );
}

export function useSetDefaultCostCenter() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  return useMutation(
    ({
      companyId,
      costCenterId,
      applyToPastDocuments,
    }: {
      companyId: string;
      costCenterId: number | null;
      applyToPastDocuments: boolean;
    }) =>
      request({
        authorizationToken: session?.token,
        url: `/companies/${companyId}/set_default_cost_center`,
        method: 'PATCH',
        data: {
          cost_center_id: costCenterId,
          apply_to_past_documents: applyToPastDocuments,
        },
      }),
    {
      onSuccess: () => {
        Notification({ type: 'success', message: 'Plan de cuenta por defecto guardado.' });
        queryClient.invalidateQueries(['companies']);
      },
    },
  );
}

export function useCompany(tax_id?: string, options?: UseQueryOptions) {
  const { session } = useAuth();
  const { enabled = true } = options || {};
  return useQuery<ApiObject<CommonHash>>(
    ['company', tax_id],
    async () => {
      const response = await request({
        authorizationToken: session?.token,
        url: `/companies/${tax_id}`,
        method: 'GET',
      });
      return response.data;
    },
    {
      enabled: session.status != 'loading' && tax_id !== undefined && tax_id !== '' && enabled,
    },
  );
}
